const ownFields = { SHIFT: ['shiftMode', 'shiftNumber', 'shiftSelection'], TAG: ['tagMode', 'tagRef', 'tagSelection'],
  PUMP: ['pumpNumbers', 'pumpNumber'], NOZZLE: ['nozzleNumbers'], TANK: ['tankNumbers', 'tankNumber'],
  FUEL: ['fuelGradeIds'], DEVICE: ['deviceMode', 'deviceType', 'deviceNumber'], CODE: ['codeMode', 'code'] }

export function emptyFacetOptions() {
  return { items: [], nextCursor: null, requestedCursor: null, hasMore: false, hasUnassigned: false, hasUnavailable: false,
    unknownDeviceCount: 0, missingCodeCount: 0, loading: false, error: '', search: '', selected: null }
}

export function facetContextKey(criteria, facet, search = '') {
  const excluded = new Set([...(ownFields[facet] ?? []), 'page', 'limit', 'shiftSelection', 'tagSelection'])
  const fields = Object.keys(criteria).filter((key) => !excluded.has(key)).sort()
  return JSON.stringify([facet, search, fields.map((key) => [key, Array.isArray(criteria[key])
    ? [...criteria[key]].sort((a, b) => a - b) : criteria[key]])])
}

export function facetItemsWithSelection(state) {
  const retained = Array.isArray(state?.selected) ? state.selected : []
  const items = state?.items ?? []
  return [...retained.filter((option) => !items.some((item) => item.number === option.number)), ...items]
}

export function retainFacetSelection(state, numbers) {
  const available = facetItemsWithSelection(state)
  state.selected = numbers.map((number) => available.find((option) => option.number === number))
    .filter(Boolean).map((option) => JSON.parse(JSON.stringify(option)))
}

// States belong to one mounted view. No option payload or search is stored outside it.
export function createFacetLifecycle({ states, getCriteria, getScope, load: read, isValid = () => true, onDenied = () => {} }) {
  const facets = Object.keys(states)
  const entries = Object.fromEntries(facets.map((facet) => [facet, { generation: 0, key: null, loaded: false }]))
  let activeScope = getScope(), disposed = false
  function reset(facet, erase = false) {
    const entry = entries[facet], state = states[facet]
    entry.generation++; entry.request?.abort(); clearTimeout(entry.timer)
    entry.pending = null; entry.loaded = false; entry.key = null
    const retained = erase ? {} : { selected: state.selected, search: state.search }
    Object.assign(state, emptyFacetOptions(), retained)
  }
  function sync() {
    if (disposed) return
    const scope = getScope()
    if (scope !== activeScope) { activeScope = scope; facets.forEach((facet) => reset(facet, true)) }
    for (const facet of facets) {
      const key = facetContextKey(getCriteria(), facet, states[facet].search)
      if (entries[facet].key !== null && key !== entries[facet].key) reset(facet)
      entries[facet].key = key
    }
  }
  function load(facet, cursor = null, { force = false } = {}) {
    if (disposed || !entries[facet]) return Promise.resolve()
    sync()
    const criteria = structuredClone(getCriteria()), state = states[facet], entry = entries[facet]
    if (!criteria.stationId || !criteria.controllerId || !isValid(criteria) || state.search.length > 80) {
      reset(facet); return Promise.resolve()
    }
    const key = facetContextKey(criteria, facet, state.search), requestScope = getScope()
    if (!force && entry.pending && entry.cursor === cursor) return entry.pending
    if (!force && entry.loaded && entry.cursor === cursor && !state.error) return Promise.resolve()
    entry.generation++; entry.request?.abort(); clearTimeout(entry.timer)
    const token = entry.generation, request = new AbortController()
    entry.request = request; entry.key = key; entry.cursor = cursor; entry.loaded = false
    Object.assign(state, { loading: true, error: '', requestedCursor: cursor, items: [], nextCursor: null, hasMore: false })
    const current = () => !disposed && !request.signal.aborted && token === entry.generation
      && requestScope === getScope() && key === facetContextKey(getCriteria(), facet, state.search)
    const pending = (async () => {
      try {
        const value = await read(criteria, facet, { cursor, search: state.search, limit: 100, signal: request.signal })
        if (!current()) return
        Object.assign(state, value)
        const selected = state.selected
        if (selected) {
          const fresh = state.items.find((item) => facet === 'TAG' ? item.ref === selected.ref : item.number === selected.number)
          if (fresh) state.selected = retainedOption(facet, fresh)
        }
        entry.loaded = true
      } catch (cause) {
        if (!current() || cause?.name === 'AbortError') return
        if ([401, 403, 404].includes(cause?.status)) { clear(); onDenied(cause) }
        else Object.assign(state, { items: [], nextCursor: null, hasMore: false, error: 'pts2FilterOptionsError' })
      } finally {
        if (token === entry.generation && !disposed) { state.loading = false; entry.pending = null }
      }
    })()
    entry.pending = pending
    return pending
  }
  function clear() { facets.forEach((facet) => reset(facet, true)) }
  return {
    sync, load, clear,
    ensure: () => Promise.all(facets.map((facet) => load(facet, entries[facet].loaded ? entries[facet].cursor : null))),
    refresh: () => Promise.all(facets.map((facet) => load(facet, null, { force: true }))),
    retry: (facet) => load(facet, states[facet].requestedCursor, { force: true }),
    search(facet, value) {
      if (disposed || !entries[facet]) return
      states[facet].search = value; reset(facet); entries[facet].key = facetContextKey(getCriteria(), facet, value)
      if (value.length <= 80) entries[facet].timer = setTimeout(() => load(facet), 150)
    },
    select(facet, option) { states[facet].selected = retainedOption(facet, option) },
    dispose() { disposed = true; clear() },
  }
}

function retainedOption(facet, option) {
  if (!option) return null
  const retained = JSON.parse(JSON.stringify(option))
  // A retained label can outlive the server's private UID retention window.
  if (facet === 'TAG' && retained.tag) retained.tag.value = null
  return retained
}
